import React from 'react';
import { useDebounce } from 'react-use';
import { Autocomplete, AutocompleteProps, TextField, TextFieldProps } from '@mui/material';

import { useUsersQuery } from '../hooks/useUsersQuery';


export type UserAutocompleteProps = Omit<AutocompleteProps<Users.User, boolean, boolean, false>, 'value' | 'isOptionEqualToValue' | 'getOptionLabel' | 'renderInput' | 'options' | 'onInputChange' | 'onChange' | 'loading'> & {
  value?: Users.User | Users.User[] | string | number | null;
  label?: TextFieldProps['label'];
  TextFieldProps?: TextFieldProps;
  onChange: (option: CVT.MaybeNull<any>) => void;
  hideAdornment?: boolean;
  params?: Users.GetListParams;
};

export const UserAutocomplete: React.FC<UserAutocompleteProps> = ({
  value,
  defaultValue,
  label,
  onChange,
  hideAdornment,
  TextFieldProps,
  params,
  ...props
}) => {
  const [inputValue, setInputValue] = React.useState('');
  const [search, setSearch] = React.useState(params?.search || '');
  const inputRef = React.useRef<HTMLInputElement>(null);

  useDebounce(() => {
    setSearch(inputValue);
  }, 500, [inputValue]);

  const { isLoading, users } = useUsersQuery({
    ...params,
    search,
  });

  const selectedValue = React.useMemo(() => {
    if (!value) return null;
    if (typeof value === 'object') return value;
    // @ts-ignore
    return users.find(user => user.id.toString() === value.toString()) || null;
  }, [value, users]);

  return (
    <Autocomplete
      {...props}
      value={selectedValue}
      defaultValue={defaultValue}
      isOptionEqualToValue={(option, value) => {
        if (!option || !value) return false;
        return option.id.toString() === (value?.id || value).toString();
      }}
      getOptionLabel={option => {
        if (!option) return '';
        return typeof option === 'string' ? option : option.displayName;
      }}
      renderInput={(props) =>
        <TextField
          {...props}
          {...TextFieldProps}
          InputProps={{
            inputRef,
            ...props.InputProps,
            ...TextFieldProps?.InputProps,
          }}
          InputLabelProps={{
            ...props.InputLabelProps,
            ...TextFieldProps?.InputLabelProps,
          }}
          label={label}
        />
      }
      inputValue={inputValue}
      onInputChange={(_, value) => setInputValue(value)}
      options={users}
      onChange={(e, value) => {
        if (typeof value !== 'string') {
          onChange(value);
        }
      }}
      filterOptions={(x) => x}
      loading={isLoading}
      noOptionsText="No users"
      slotProps={{
        paper: {
          elevation: 2,
        },
      }}
    />
  );
};
